import { z } from 'zod'

export const GUEST_UPGRADE_ID_HEADER = 'x-argus-guest-upgrade-id'

export const gameIdSchema = z
  .string()
  .min(3)
  .regex(/^[^/\s]+\/[^/\s]+$/)
  .describe('Canonical organization/project identifier')

export const handoffIdSchema = z.string().length(32)
export const upgradeIdSchema = z.string().length(32)

const emailAddressSchema = z
  .string()
  .min(1, 'Enter your email address')
  .pipe(z.email('Enter an email address in the format name@example.com'))

export const loginSchema = z.object({
  email: emailAddressSchema,
  password: z
    .string()
    .min(8, { message: 'Password must be at least 8 characters' })
    .max(100, { message: 'Password must be at most 100 characters' }),
})

// New passwords only: sign-in keeps the 8-character minimum so existing passwords still work.
export const PASSWORD_REQUIREMENTS = [
  {
    label: 'At least 8 characters',
    message: 'Password must be at least 8 characters',
    test: (password: string) => password.length >= 8,
  },
  {
    label: 'A lowercase letter',
    message: 'Password must include a lowercase letter',
    test: (password: string) => /[a-z]/.test(password),
  },
  {
    label: 'An uppercase letter',
    message: 'Password must include an uppercase letter',
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    label: 'A number',
    message: 'Password must include a number',
    test: (password: string) => /\d/.test(password),
  },
  {
    label: 'A symbol',
    message: 'Password must include a symbol',
    test: (password: string) => /[^A-Za-z0-9\s]/.test(password),
  },
] as const

export const newPasswordSchema = PASSWORD_REQUIREMENTS.reduce<z.ZodType<string, string>>(
  (schema, { test, message }) => schema.refine(test, message),
  z.string().max(100, { message: 'Password must be at most 100 characters' }),
)

export const nameSchema = z.object({
  name: z.string().min(1, { message: 'Enter your name' }),
})

export const registerSchema = z.object({
  firstName: z.string().trim().min(1, { message: 'Enter your first name' }),
  lastName: z.string().trim().min(1, { message: 'Enter your last name' }),
  email: emailAddressSchema,
  password: newPasswordSchema,
})

export const verifySchema = z.object({
  email: emailAddressSchema,
  otp: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code'),
})

export const forgotPasswordSchema = z.object({
  email: emailAddressSchema,
})

export const resetPasswordSchema = z.object({
  password: newPasswordSchema,
})

export type LoginParams = z.infer<typeof loginSchema>
export type RegisterParams = z.infer<typeof registerSchema>
export type VerifyParams = z.infer<typeof verifySchema>
export type ForgotPasswordParams = z.infer<typeof forgotPasswordSchema>
export type ResetPasswordParams = z.infer<typeof resetPasswordSchema>
